import {
  isAfter,
  isBefore,
  isEqual,
  differenceInMinutes,
  startOfWeek,
  addDays,
  format,
} from 'date-fns';

const minutesToHoursAndMinutes = (minutes: number): string => {
  const hours: number = Math.floor(minutes / 60);
  const remainingMinutes: number = minutes % 60;

  if (hours === 0) {
    return `${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'}`;
  } else if (remainingMinutes === 0) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  } else {
    return `${hours} hour${hours === 1 ? '' : 's'} ${remainingMinutes} minute${
      remainingMinutes === 1 ? '' : 's'
    }`;
  }
};

export const getTimeRemainingMessage = (
  currentTime: Date,
  startTime?: Date,
  endTime?: Date
): string => {
  const _startTime = startTime || currentTime;
  const _endTime = endTime || currentTime;

  if (isAfter(_startTime, currentTime)) {
    const diffMinutes = differenceInMinutes(_startTime, currentTime);
    const startsInMinutes = minutesToHoursAndMinutes(diffMinutes);

    return `Starts in ${startsInMinutes}`;
  }

  const difference = differenceInMinutes(_endTime, currentTime);
  const differenceStr = minutesToHoursAndMinutes(difference);

  if (difference <= 0) {
    return 'Completed';
  }

  return `${differenceStr} to go!`;
};

export const getAverageTimeSpentMessage = (currentTime: Date): string => {
  const startTime = convertTimeStringToDate('9:00 AM');
  const sixHours = 6 * 60;

  const difference = Math.max(Math.min(differenceInMinutes(currentTime, startTime), sixHours), 0);
  const differenceStr = minutesToHoursAndMinutes(difference);

  return `${differenceStr} spent learning!`;
};

export const convertTimeStringToDate = (timeString: string): Date => {
  // Assuming current date
  const currentDate = new Date();
  // Get the time string
  const [time, period] = timeString.split(' ');
  const [hours, minutes] = time.split(':').map(Number);

  // Set hours, minutes, and seconds
  currentDate.setHours(hours);
  currentDate.setMinutes(minutes);
  currentDate.setSeconds(0); // Optional, you might want to reset seconds too

  // Adjust for AM/PM
  if (period === 'PM' && hours < 12) {
    currentDate.setHours(currentDate.getHours() + 12);
  } else if (period === 'AM' && hours === 12) {
    currentDate.setHours(0);
  }

  return currentDate;
};

export const differenceInHours = (minutesDifference: number) => {
  // Convert minutes to "hh:mm hrs" format
  const hours: number = Math.floor(minutesDifference / 60);
  const minutes: number = minutesDifference % 60;

  const formattedTime: string = `${hours}:${minutes < 10 ? '0' : ''}${minutes} hrs`;

  return formattedTime;
};

export const isCurrentTimeInRange = (currentTime: Date, startTime: Date, endTime: Date) =>
  (isEqual(currentTime, startTime) || isAfter(currentTime, startTime)) &&
  isBefore(currentTime, endTime);

export const isTimePast = (currentTime: Date, endTime: Date) => isAfter(currentTime, endTime);

export const getCurrentWeekStartEndDates = (): {
  startDate: string;
  endDate: string;
  todaysDate: string;
} => {
  // Date For Monday
  const startDate = startOfWeek(new Date(), { weekStartsOn: 1 });

  // Date for Friday
  const endDate = addDays(startDate, 4);

  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
    todaysDate: format(new Date(), 'yyyy-MM-dd'),
  };
};
